import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../../axios';
import Modal from '../../../Other/Modal';
import { 
  FaBookOpen, 
  FaUser, 
  FaCalendarAlt, 
  FaClock, 
  FaExclamationTriangle, 
  FaCheckCircle, 
  FaInfoCircle, 
  FaArrowLeft,
  FaEdit,
  FaSearch,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';

const getAvatarBgColor = (name) => {
  const colors = [
    'bg-indigo-50 text-indigo-700 border-indigo-100/80',
    'bg-emerald-50 text-emerald-700 border-emerald-100/80',
    'bg-amber-50 text-amber-700 border-amber-100/80',
    'bg-rose-50 text-rose-700 border-rose-100/80',
    'bg-sky-50 text-sky-700 border-sky-100/80',
    'bg-violet-50 text-violet-700 border-violet-100/80',
    'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100/80'
  ];
  if (!name) return 'bg-slate-50 text-slate-700 border-slate-100';
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

const InstructorMultiSelect = ({
  instructors,
  selectedIds,
  onChange,
  searchTerm,
  onSearchChange,
  highlightError,
  disabled,
  placeholder = "Select instructors...",
  instructorId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (id, checked) => {
    if (checked) {
      onChange([...selectedIds, id]);
    } else {
      onChange(selectedIds.filter((x) => x !== id));
    }
  };

  const filtered = instructors.filter((inst) => {
    const term = searchTerm.toLowerCase();
    const name = (inst.name || '').toLowerCase();
    const email = (inst.email || '').toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  const selectedInstructorsList = instructors.filter((inst) =>
    selectedIds.includes(Number(inst.id)) || (instructorId && Number(inst.id) === Number(instructorId))
  );

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Trigger Button */}
      <div
        onClick={handleToggle}
        className={`flex items-center justify-between min-h-[42px] w-full border rounded-lg px-3 py-1.5 bg-slate-50 text-xs cursor-pointer transition-all ${
          disabled ? 'opacity-80 cursor-not-allowed bg-slate-100 border-slate-200' : 'hover:border-slate-300 hover:bg-slate-50/50'
        } ${
          highlightError
            ? 'border-red-500 ring-2 ring-red-100 bg-red-50/50'
            : isOpen
            ? 'border-slate-400 ring-2 ring-slate-100 bg-white'
            : 'border-slate-200'
        }`}
      >
        <div className="flex flex-wrap gap-1.5 items-center mr-2 max-w-[90%] py-0.5">
          {selectedInstructorsList.length === 0 ? (
            <span className="text-slate-400 text-xs font-semibold">{placeholder}</span>
          ) : (
            selectedInstructorsList.map((inst) => {
              const initials = inst.name
                ? inst.name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()
                : 'IN';
              const avatarStyle = getAvatarBgColor(inst.name);
              return (
                <span
                  key={inst.id}
                  className={`inline-flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-md border text-[10px] font-bold ${avatarStyle}`}
                >
                  <span className="w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center text-[7px] font-black border border-inherit">
                    {initials}
                  </span>
                  <span className="truncate max-w-[120px]">{inst.name}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(Number(inst.id), false);
                      }}
                      className="ml-1 text-slate-400 hover:text-red-655 transition-colors focus:outline-none text-xs font-black"
                    >
                      &times;
                    </button>
                  )}
                </span>
              );
            })
          )}
        </div>
        <div className="flex items-center text-slate-400 flex-shrink-0 ml-auto">
          {isOpen ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
        </div>
      </div>

      {/* Dropdown Popover */}
      {isOpen && (
        <div className=" z-50 left-0 w-80 max-w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden animate-fadeIn">
          {/* Search Box */}
          <div className="p-2.5 border-b border-slate-100 bg-slate-50/50">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                <FaSearch className="w-3 h-3" />
              </span>
              <input
                type="text"
                placeholder="Search instructors by name or email..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full pl-8 pr-8 py-1.5 text-xs bg-white border border-slate-200 rounded-lg placeholder:text-slate-400 text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400 font-semibold"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSearchChange('');
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-655 text-sm font-bold"
                >
                  &times;
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-52 overflow-y-auto p-2 space-y-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-4 text-center">No matching instructors found.</p>
            ) : (
              filtered.map((inst) => {
                const initials = inst.name
                  ? inst.name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()
                  : 'IN';
                const avatarStyle = getAvatarBgColor(inst.name);
                const isChecked = selectedIds.includes(Number(inst.id));
                return (
                  <label
                    key={inst.id}
                    className={`flex items-center justify-between p-2 rounded-xl border transition-all cursor-pointer select-none ${
                      isChecked ? 'bg-slate-50 border-slate-250/70 shadow-3xs' : 'border-transparent hover:bg-slate-100/50'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          handleSelect(Number(inst.id), e.target.checked);
                        }}
                        className="rounded text-slate-750 focus:ring-0 w-4 h-4 accent-slate-700 cursor-pointer"
                      />
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black border flex-shrink-0 ${avatarStyle}`}>
                        {initials}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-800">{inst.name}</span>
                        <span className="text-[10px] text-slate-400">{inst.email}</span>
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const GroupBatchCreator = ({ onClose, instructorId }) => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedCourseName, setSelectedCourseName] = useState('');

  // Instructors Available search filters
  const [instructorSearch, setInstructorSearch] = useState('');
  const [editInstructorSearch, setEditInstructorSearch] = useState('');

  const [instructors, setInstructors] = useState([]);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isWeekend, setIsWeekend] = useState(false); // Daily/Weekend checkbox

  const [courseBatches, setCourseBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [conflictError, setConflictError] = useState(false);
  const [highlightInstructor, setHighlightInstructor] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showOccupancyModal, setShowOccupancyModal] = useState(false);
  const [activeOccupancyTab, setActiveOccupancyTab] = useState(null);

  // All batches for live conflict checking
  const [allBatches, setAllBatches] = useState([]);
  const [timingConflict, setTimingConflict] = useState(null);

  // Edit Batch States
  const [editingBatch, setEditingBatch] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editIsWeekend, setEditIsWeekend] = useState(false);
  const [editInstructors, setEditInstructors] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [editConflictError, setEditConflictError] = useState(false);

  // Helpers for conflict detection
  const toMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = t.substring(0, 5).split(':').map(Number);
    return h * 60 + m;
  };
  const normalizeDate = (d) => (d ? d.split('T')[0] : '');

  // 1. Fetch all courses + all existing batches on mount
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axiosInstance.get('/course');
        const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
        setCourses(list);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };
    const fetchAllBatchesForConflict = async () => {
      try {
        const res = await axiosInstance.get('/admin/group-batch-list');
        setAllBatches(res.data?.data || []);
      } catch (error) {
        console.error('Error fetching batches for conflict check:', error);
      }
    };
    fetchCourses();
    fetchAllBatchesForConflict();
  }, []);

  useEffect(() => {
    if (instructorId) {
      setSelectedInstructors([Number(instructorId)]);
    }
  }, [instructorId, selectedCourse]);

  // Live conflict check whenever instructor/dates/times change
  useEffect(() => {
    if (selectedInstructors.length === 0 || !startDate || !endDate || !startTime || !endTime) {
      setTimingConflict(null);
      return;
    }
    const conflict = allBatches.find((batch) => {
      const hasConflictInstructor = selectedInstructors.some((selectedId) =>
        batch.instructors?.some((i) => Number(i.id) === Number(selectedId)) || Number(batch.instructorId) === Number(selectedId)
      );
      if (!hasConflictInstructor || batch.status === 'completed') return false;

      const bStart = normalizeDate(batch.startDate);
      const bEnd   = normalizeDate(batch.endDate);
      const datesClash = startDate <= bEnd && endDate >= bStart;
      const timesClash =
        batch.startTime && batch.endTime &&
        toMinutes(startTime) < toMinutes(batch.endTime) &&
        toMinutes(endTime)   > toMinutes(batch.startTime);

      return datesClash && timesClash;
    });
    setTimingConflict(conflict || null);
  }, [selectedInstructors, startDate, endDate, startTime, endTime, allBatches]);

  // Sync active occupancy tab when modal is opened or instructors are selected
  useEffect(() => {
    if (selectedInstructors.length > 0) {
      if (!activeOccupancyTab || !selectedInstructors.includes(Number(activeOccupancyTab))) {
        setActiveOccupancyTab(Number(selectedInstructors[0]));
      }
    } else {
      setActiveOccupancyTab(null);
    }
  }, [selectedInstructors, showOccupancyModal]);

  // 2. Fetch instructors for the selected course
  const fetchCourseInstructors = async (courseName) => {
    setLoadingInstructors(true);
    try {
      const res = await axiosInstance.get('/admin/instructors', {
        params: { CourseName: courseName }
      });
      const list = res.data?.data || [];
      setInstructors(list);
    } catch (error) {
      console.error('Error fetching instructors by course:', error);
      setInstructors([]);
    } finally {
      setLoadingInstructors(false);
    }
  };

  useEffect(() => {
    if (selectedCourseName) {
      if (!instructorId) {
        setSelectedInstructors([]);
      }
      setConflictError(false);
      setHighlightInstructor(false);
      fetchCourseInstructors(selectedCourseName);
    } else {
      setInstructors([]);
      if (!instructorId) {
        setSelectedInstructors([]);
      }
    }
  }, [selectedCourseName, instructorId]);

  // 3. Load existing batches for this selected course
  const loadCourseBatches = async (cId) => {
    if (!cId) return;
    setLoadingBatches(true);
    try {
      const res = await axiosInstance.get('/admin/group-batch-list', {
        params: { courseId: cId }
      });
      let data = res.data?.data || [];
      if (instructorId) {
        data = data.filter(batch => 
          Number(batch.instructorId) === Number(instructorId) ||
          batch.instructors?.some(inst => Number(inst.id) === Number(instructorId))
        );
      }
      setCourseBatches(data);
    } catch (error) {
      console.error('Error loading course batches:', error);
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    if (selectedCourse) {
      loadCourseBatches(selectedCourse);
    } else {
      setCourseBatches([]);
    }
  }, [selectedCourse]);

  // 4. Handle course dropdown changes
  const handleCourseChange = (e) => {
    const val = e.target.value;
    setSelectedCourse(val);
    if (val) {
      const matched = courses.find(c => String(c.ID || c.id) === String(val));
      setSelectedCourseName(matched ? matched.CourseName : '');
    } else {
      setSelectedCourseName('');
    }
  };

  // 5. Submit Batch Creation Form
  const handleSubmit = async () => {
    if (!selectedCourse || selectedInstructors.length === 0 || !startDate || !endDate || !startTime || !endTime) {
      alert('Please fill out all the fields.');
      return;
    }

    setLoading(true);
    setMessage('');
    setConflictError(false);
    setHighlightInstructor(false);

    try {
      const response = await axiosInstance.post(`/admin/create-group-batch`, {
        startDate,
        endDate,
        courseId: selectedCourse,
        instructorId: selectedInstructors[0],
        instructorIds: selectedInstructors,
        startTime,
        endTime,
        daysType: isWeekend ? 'Weekend' : 'Daily'
      });

      if (response.data.error) {
        setMessage(response.data.message || 'Failed to create batch.');
      } else {
        setMessage(response.data.message || 'Batch groups successfully created!');
        loadCourseBatches(selectedCourse);
        // Refresh conflict-check pool with the newly created batch
        const refreshed = await axiosInstance.get('/admin/group-batch-list');
        setAllBatches(refreshed.data?.data || []);
        setStartTime('');
        setEndTime('');
      }
    } catch (error) {
      console.error('Error creating batch:', error);
      const errMsg = error.response?.data?.message || '';
      const isConflict = error.response?.data?.code === 'DUPLICATE_BATCH_TIMING' || errMsg.toLowerCase().includes('already exists') || errMsg.toLowerCase().includes('same instructor');

      if (isConflict) {
        setConflictError(true);
        setHighlightInstructor(true);
        setMessage(errMsg || 'A batch already exists for this course with the same instructor and timing. Please select a different instructor.');
        setShowConflictModal(true);
      } else {
        setMessage(errMsg || 'Failed to create batch. Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 6. Handle Edit Click
  const handleEditClick = (batch) => {
    setEditingBatch(batch);
    setEditStartDate(batch.startDate || '');
    setEditEndDate(batch.endDate || '');
    setEditStartTime(batch.startTime || '');
    setEditEndTime(batch.endTime || '');
    setEditIsWeekend(batch.daysType === 'Weekend');
    
    const instIds = batch.instructors?.map(i => Number(i.id)) || (batch.instructorId ? [Number(batch.instructorId)] : []);
    setEditInstructors(instIds);
    
    setEditMessage('');
    setEditConflictError(false);
    setShowEditModal(true);
  };

  // 7. Submit Batch Edit Update Form
  const handleUpdateSubmit = async () => {
    if (!editStartDate || !editEndDate || !editStartTime || !editEndTime || editInstructors.length === 0) {
      alert('Please fill out all the fields.');
      return;
    }

    setEditLoading(true);
    setEditMessage('');
    setEditConflictError(false);

    try {
      const response = await axiosInstance.put(`/admin/group-batch/${editingBatch.groupId}`, {
        startDate: editStartDate,
        endDate: editEndDate,
        startTime: editStartTime,
        endTime: editEndTime,
        daysType: editIsWeekend ? 'Weekend' : 'Daily',
        instructorId: editInstructors[0],
        instructorIds: editInstructors
      });

      if (response.data.error) {
        setEditMessage(response.data.message || 'Failed to update batch.');
      } else {
        setEditMessage('Batch details successfully updated!');
        // Refresh inline batches list
        loadCourseBatches(selectedCourse);
        // Auto close edit modal after short delay
        setTimeout(() => {
          setShowEditModal(false);
          setEditingBatch(null);
          setEditInstructorSearch('');
        }, 1200);
      }
    } catch (error) {
      console.error('Error updating batch:', error);
      const errMsg = error.response?.data?.message || '';
      const isConflict = error.response?.data?.code === 'DUPLICATE_BATCH_TIMING' || errMsg.toLowerCase().includes('already exists') || errMsg.toLowerCase().includes('same instructor');

      if (isConflict) {
        setEditConflictError(true);
        setEditMessage(errMsg || 'A batch already exists for this course with the same instructor and timing. Please select a different instructor.');
      } else {
        setEditMessage(errMsg || 'Failed to update batch. Something went wrong.');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Logic to determine if Step 3 (Time & Frequency) should be revealed
  const isSecondStepComplete = selectedCourse && selectedInstructors.length > 0 && startDate && endDate;

  // Filter lists based on search terms
  const filteredInstructors = instructors.filter(inst => {
    const term = instructorSearch.toLowerCase();
    const name = (inst.name || '').toLowerCase();
    const email = (inst.email || '').toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  const filteredEditInstructors = instructors.filter(inst => {
    const term = editInstructorSearch.toLowerCase();
    const name = (inst.name || '').toLowerCase();
    const email = (inst.email || '').toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  return (
    <div className="max-w-5xl mx-auto bg-slate-50 border border-slate-200 shadow-lg rounded-2xl p-8 transition-all duration-300">
      
      {/* Form Header */}
      <div className="flex items-center justify-between pb-6 mb-8 border-b border-slate-200">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Create New Group Batch</h2>
          <p className="text-sm text-slate-500 mt-1">Setup course batch details, schedule dates, times, and assign instructors.</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-100 hover:text-slate-800 transition-colors"
          >
            <FaArrowLeft className="text-xs" /> Back
          </button>
        )}
      </div>

      {/* Stepper Progress Indicator */}
      {selectedCourse && (
        <div className="flex items-center justify-between bg-white border border-slate-150 p-4 rounded-xl shadow-3xs mb-6 animate-fadeIn">
          <div className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              selectedCourse ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-400'
            }`}>1</span>
            <span className="text-xs font-bold text-slate-750">Select Course</span>
          </div>
          <div className="flex-1 h-0.5 bg-slate-200 mx-4 max-w-[80px]" />
          <div className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              (selectedInstructors.length > 0 && startDate && endDate) 
                ? 'bg-slate-800 text-white' 
                : selectedCourse ? 'bg-slate-100 border border-slate-300 text-slate-650 ring-2 ring-slate-100' : 'bg-slate-100 text-slate-400'
            }`}>2</span>
            <span className="text-xs font-bold text-slate-700">Assign Instructor & Dates</span>
          </div>
          <div className="flex-1 h-0.5 bg-slate-200 mx-4 max-w-[80px]" />
          <div className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              isSecondStepComplete ? 'bg-slate-100 border border-slate-350 text-slate-650 ring-2 ring-slate-100' : 'bg-slate-100 text-slate-400'
            }`}>3</span>
            <span className="text-xs font-bold text-slate-750">Class Timings</span>
          </div>
        </div>
      )}

      {/* Main Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* LEFT COLUMN: Progressive Setup Card */}
        <div className="space-y-6 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          
          {/* STEP 1: Course Selection */}
          <div>
            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
              <FaBookOpen className="text-slate-500 text-xs" /> Select Course <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedCourse}
              onChange={handleCourseChange}
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 bg-slate-50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:bg-white transition-all text-sm"
            >
              <option value="">-- Choose Course --</option>
              {courses.map((course) => (
                <option key={course.ID || course.id} value={course.ID || course.id}>
                  {course.CourseName}
                </option>
              ))}
            </select>
          </div>

          {/* STEP 2: Instructor & Dates (Reveals once Course is selected) */}
          {selectedCourse && (
            <div className="space-y-6 pt-4 border-t border-slate-100 animate-fadeIn">
              
              {/* Instructor Selector */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <FaUser className="text-slate-500 text-xs" /> Assign Instructor <span className="text-red-500">*</span>
                  </label>
                </div>
                
                {loadingInstructors ? (
                  <p className="text-xs text-slate-500 animate-pulse">Loading instructors...</p>
                ) : (
                  <InstructorMultiSelect
                    instructors={instructors}
                    selectedIds={selectedInstructors}
                    onChange={(newSelected) => {
                      setConflictError(false);
                      setHighlightInstructor(false);
                      setSelectedInstructors(newSelected);
                      if (newSelected.length > 0) {
                        setShowOccupancyModal(true);
                      }
                    }}
                    searchTerm={instructorSearch}
                    onSearchChange={setInstructorSearch}
                    highlightError={highlightInstructor}
                    disabled={!!instructorId}
                    instructorId={instructorId}
                    placeholder="Select instructors to assign..."
                  />
                )}
                {highlightInstructor && (
                  <p className="text-xs text-red-600 mt-1 font-medium flex items-center gap-1">
                    <FaExclamationTriangle className="text-2xs" /> timing conflict! Please select a different instructor.
                  </p>
                )}

                {/* VIEW SCHEDULE COMMITMENTS BUTTON */}
                {selectedInstructors.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowOccupancyModal(true)}
                    className="mt-2.5 flex items-center justify-center gap-1.5 w-full py-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 hover:border-slate-400 rounded-lg text-xs font-bold text-slate-700 transition-colors shadow-3xs cursor-pointer"
                  >
                    <FaClock className="text-slate-500" /> View Occupancy & Active Schedules
                  </button>
                )}
              </div>

              {/* Date Pickers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                    <FaCalendarAlt className="text-slate-500 text-xs" /> Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2 bg-slate-50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:bg-white transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                    <FaCalendarAlt className="text-slate-500 text-xs" /> End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2 bg-slate-50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:bg-white transition-all text-sm"
                  />
                </div>
              </div>

            </div>
          )}

          {/* STEP 3: Time & Frequency (Reveals once Steps 1 & 2 are complete) */}
          {isSecondStepComplete && (
            <div className="space-y-6 pt-4 border-t border-slate-100 animate-fadeIn">
              
              {/* Time Pickers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                    <FaClock className="text-slate-500 text-xs" /> Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2 bg-slate-50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:bg-white transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                    <FaClock className="text-slate-500 text-xs" /> End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2 bg-slate-50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:bg-white transition-all text-sm"
                  />
                </div>
              </div>

              {/* Daily / Weekend Checkbox */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex items-center justify-between shadow-inner">
                <div>
                  <h4 className="text-sm font-bold text-slate-800">Class Frequency</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Toggle between Daily lectures or Weekend schedule.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isWeekend}
                    onChange={(e) => setIsWeekend(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-6 after:transition-all peer-checked:bg-slate-700"></div>
                  <span className="ml-3 text-sm font-semibold text-slate-700 w-16">
                    {isWeekend ? 'Weekend' : 'Daily'}
                  </span>
                </label>
              </div>

              {/* Live Timing Conflict Warning */}
              {timingConflict && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 flex gap-3 items-start animate-fadeIn">
                  <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0 text-base" />
                  <div>
                    <p className="font-bold text-sm">Instructor Timing Conflict</p>
                    <p className="text-xs mt-1 leading-relaxed">
                      This instructor is already assigned to{' '}
                      <strong>"{timingConflict.batchName}"</strong> from{' '}
                      <strong>{timingConflict.startTime} – {timingConflict.endTime}</strong>{' '}
                      ({normalizeDate(timingConflict.startDate)} → {normalizeDate(timingConflict.endDate)}).
                      The new batch overlaps that schedule — please choose a different time slot or select another instructor.
                    </p>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <button
                onClick={handleSubmit}
                disabled={loading || !!timingConflict}
                className="w-full py-3 rounded-lg text-sm font-bold bg-slate-700 hover:bg-slate-800 text-white shadow-md disabled:bg-slate-300 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
              >
                {loading ? 'Generating Batches...' : 'Create Batch Groups'}
              </button>

            </div>
          )}

          {/* Feedback Messages */}
          {message && (
            <div 
              className={`p-4 rounded-xl text-sm border flex gap-3 items-start animate-slideUp ${
                conflictError 
                  ? 'bg-red-50 border-red-200 text-red-800' 
                  : message.toLowerCase().includes('success') 
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                    : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}
            >
              {conflictError ? (
                <FaExclamationTriangle className="text-red-500 mt-0.5 text-base flex-shrink-0" />
              ) : message.toLowerCase().includes('success') ? (
                <FaCheckCircle className="text-emerald-500 mt-0.5 text-base flex-shrink-0" />
              ) : (
                <FaInfoCircle className="text-blue-500 mt-0.5 text-base flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-semibold">{conflictError ? 'Conflict Error' : 'Status Alert'}</p>
                <p className="text-xs mt-1 leading-relaxed">{message}</p>
              </div>
            </div>
          )}

        </div>

        {/* RIGHT COLUMN: Inline Existing Course Batches */}
        <div className="flex flex-col h-full bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-slate-800">
              {selectedCourseName ? `Batches for: ${selectedCourseName}` : 'Existing Batches'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {selectedCourseName ? 'All active/upcoming batches scheduled for this course.' : 'Select a course to view scheduled batches.'}
            </p>
          </div>

          <div className="flex-1 min-h-[300px] overflow-y-auto border border-slate-100 rounded-lg">
            {!selectedCourse ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center text-slate-400">
                <FaBookOpen className="text-3xl mb-2 text-slate-300" />
                <p className="text-sm font-medium">No Course Selected</p>
                <p className="text-xs mt-1 max-w-[200px]">Select a course on the left to pull scheduled batches.</p>
              </div>
            ) : loadingBatches ? (
              <div className="flex items-center justify-center h-full p-8 text-slate-500">
                <p className="text-sm animate-pulse font-medium">Loading course batches...</p>
              </div>
            ) : courseBatches.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center text-slate-400">
                <FaInfoCircle className="text-3xl mb-2 text-slate-300" />
                <p className="text-sm font-medium">No Batches Yet</p>
                <p className="text-xs mt-1 max-w-[200px]">No schedule created for this course. Fill the form to create one.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100/60">
                {courseBatches.map((batch, index) => {
                  return (
                    <div key={index} className="p-5 hover:bg-slate-50/50 transition-all relative group flex flex-col gap-2">
                      <div className="flex justify-between items-start gap-2 pr-8">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-150 flex items-center justify-center text-slate-500">
                            <FaBookOpen className="w-3.5 h-3.5 text-slate-400" />
                          </div>
                          <h4 className="text-sm font-black text-slate-800 leading-tight">{batch.batchName}</h4>
                        </div>
                        <span className={`text-[10px] font-black tracking-wide px-2.5 py-0.5 rounded-full border uppercase ${
                          batch.status === 'completed' ? 'bg-slate-50 text-slate-500 border-slate-205' :
                          batch.status === 'ongoing' ? 'bg-emerald-50 text-emerald-700 border-emerald-200/50' :
                          'bg-amber-50 text-amber-700 border-amber-200/50'
                        }`}>
                          {batch.status}
                        </span>
                      </div>
                      
                      {/* Edit Button */}
                      <button
                        onClick={() => handleEditClick(batch)}
                        title="Edit Batch Settings"
                        className="absolute right-4 top-4 p-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 border border-slate-250 border-slate-200 rounded-lg cursor-pointer flex items-center justify-center shadow-3xs transition-all opacity-0 group-hover:opacity-100"
                      >
                        <FaEdit className="text-xs" />
                      </button>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-500 mt-1.5">
                        <div className="flex items-center gap-1.5 font-bold">
                          <FaCalendarAlt className="text-slate-400" />
                          <span>{batch.startDate} to {batch.endDate}</span>
                        </div>
                        {batch.startTime && (
                          <div className="flex items-center gap-1.5 font-bold">
                            <FaClock className="text-slate-400" />
                            <span>{batch.startTime} - {batch.endTime} ({batch.daysType || 'Daily'})</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 col-span-1 sm:col-span-2 mt-1 border-t border-slate-100/50 pt-1.5 flex-wrap">
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-405">Instructors:</span>
                          <div className="flex flex-wrap gap-1">
                            {batch.instructors && batch.instructors.length > 0 ? (
                              batch.instructors.map(inst => {
                                const initials = inst.name ? inst.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'IN';
                                const avatarStyle = getAvatarBgColor(inst.name);
                                return (
                                  <span key={inst.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold ${avatarStyle}`}>
                                    <span className="w-3.5 h-3.5 rounded-full bg-white flex items-center justify-center text-[7px] font-black">{initials}</span>
                                    {inst.name}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-xs text-slate-400 font-medium italic">None assigned</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Timing Conflict Custom Modal Popup */}
      <Modal isOpen={showConflictModal} onClose={() => setShowConflictModal(false)} size="md">
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 border border-red-200">
            <FaExclamationTriangle className="text-red-500 text-lg animate-bounce" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-2">Timing Conflict</h3>
          <p className="text-sm text-slate-600 mb-6 leading-relaxed">
            A batch already exists for this course with the same instructor and timing.<br/>
            <span className="font-bold text-slate-800">Please select other instructors or pick other timing.</span>
          </p>
          <button
            onClick={() => setShowConflictModal(false)}
            className="w-full py-2.5 rounded-lg text-sm font-bold bg-slate-700 hover:bg-slate-800 text-white shadow-md transition-all duration-200 cursor-pointer"
          >
            Got it, change instructor
          </button>
        </div>
      </Modal>

      {/* Edit Batch Details Modal Popup */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setEditingBatch(null); setEditInstructorSearch(''); }} size="lg">
        {editingBatch && (
          <div className="p-6 bg-slate-50 rounded-xl">
            <div className="pb-4 mb-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FaEdit className="text-slate-700 w-5 h-5" /> Edit Batch Settings
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Modify dates, time slots, or re-assign instructor for this batch.</p>
            </div>

            <div className="space-y-4">
              {/* Course Name (Read-Only) */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Course Name</label>
                <div className="bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 font-bold truncate shadow-3xs">
                  {selectedCourseName}
                </div>
              </div>

              {/* Instructor Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-505 uppercase tracking-wider mb-1.5">Assign Instructors</label>
                <InstructorMultiSelect
                  instructors={instructors}
                  selectedIds={editInstructors}
                  onChange={(newSelected) => {
                    setEditConflictError(false);
                    setEditInstructors(newSelected);
                  }}
                  searchTerm={editInstructorSearch}
                  onSearchChange={setEditInstructorSearch}
                  highlightError={editConflictError}
                  disabled={!!instructorId}
                  instructorId={instructorId}
                  placeholder="Select instructors to assign..."
                />
              </div>

              {/* Start & End Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                  />
                </div>
              </div>

              {/* Start & End Times */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={editEndTime}
                    onChange={(e) => setEditEndTime(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 text-sm"
                  />
                </div>
              </div>

              {/* Class Frequency */}
              <div className="bg-white p-3 rounded-lg border border-slate-200 flex items-center justify-between shadow-sm">
                <div>
                  <h4 className="text-xs font-bold text-slate-800">Frequency</h4>
                  <p className="text-[10px] text-slate-500">Daily lectures or Weekend classes.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={editIsWeekend}
                    onChange={(e) => setEditIsWeekend(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-slate-700"></div>
                  <span className="ml-2 text-xs font-semibold text-slate-700 w-14">
                    {editIsWeekend ? 'Weekend' : 'Daily'}
                  </span>
                </label>
              </div>

              {/* Edit Feedback Message */}
              {editMessage && (
                <div 
                  className={`p-3 rounded-lg text-xs border flex gap-2 items-start animate-slideUp ${
                    editConflictError 
                      ? 'bg-red-50 border-red-200 text-red-800' 
                      : editMessage.toLowerCase().includes('success') 
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                        : 'bg-blue-50 border-blue-200 text-blue-800'
                  }`}
                >
                  <FaInfoCircle className="mt-0.5 flex-shrink-0 text-sm" />
                  <p className="leading-normal">{editMessage}</p>
                </div>
              )}

              {/* Actions Footer */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={handleUpdateSubmit}
                  disabled={editLoading}
                  className="flex-1 py-2 rounded-lg text-sm font-bold bg-slate-700 hover:bg-slate-800 text-white shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => { setShowEditModal(false); setEditingBatch(null); }}
                  disabled={editLoading}
                  className="flex-1 py-2 rounded-lg text-sm font-bold bg-slate-200 hover:bg-slate-300 text-slate-700 shadow-sm transition-all duration-200 cursor-pointer"
                >
                  Cancel
                </button>
              </div>

            </div>
          </div>
        )}
      </Modal>
       {/* Selected Instructors Occupancy Modal */}
      <Modal isOpen={showOccupancyModal} onClose={() => setShowOccupancyModal(false)} size="lg">
        <div className="p-6 bg-slate-50 rounded-xl">
          <div className="pb-4 mb-4 border-b border-slate-250 flex justify-between items-center">
            <div>
              <h3 className="text-xl font-black text-slate-850 flex items-center gap-2">
                <FaClock className="text-slate-750 w-5 h-5" /> Instructor Occupancy & Schedules
              </h3>
              <p className="text-xs text-slate-500 font-bold mt-0.5">Active and upcoming batch commitments for the selected instructors.</p>
            </div>
            <span className="text-xs font-black uppercase bg-slate-200/80 px-3 py-1.5 rounded-full text-slate-700 shadow-3xs">
              {selectedInstructors.length} Instructor(s)
            </span>
          </div>
          {/* Instructor Tabs */}
          <div className="flex border-b border-slate-200 overflow-x-auto pb-px mb-5 gap-2 scrollbar-none">
            {selectedInstructors.map(instId => {
              const inst = instructors.find(i => Number(i.id) === Number(instId));
              if (!inst) return null;
              const isActive = Number(activeOccupancyTab) === Number(instId);

              // Calculate metrics for tab preview bubble
              const instBatches = allBatches.filter(batch => 
                (Number(batch.instructorId) === Number(instId) ||
                batch.instructors?.some(i => Number(i.id) === Number(instId))) &&
                batch.status !== 'completed'
              );
              const dailyCount = instBatches.filter(b => (b.daysType || '').toLowerCase() === 'daily').length;
              const weekendCount = instBatches.filter(b => (b.daysType || '').toLowerCase() === 'weekend').length;

              return (
                <button
                  key={instId}
                  type="button"
                  onClick={() => setActiveOccupancyTab(Number(instId))}
                  className={`px-5 py-2.5 text-sm uppercase tracking-wider font-extrabold border-b-2 transition-all duration-150 cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                    isActive 
                      ? 'border-slate-800 text-slate-850 text-base scale-105 font-black' 
                      : 'border-transparent text-slate-400 hover:text-slate-650'
                  }`}
                >
                  <span>{inst.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    isActive 
                      ? 'bg-slate-800 text-white' 
                      : 'bg-slate-100 text-slate-500 border border-slate-200'
                  }`}>
                    {instBatches.length} ({dailyCount}D/{weekendCount}W)
                  </span>
                </button>
              );
            })}
          </div>

          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1.5 scrollbar-thin">
            {(() => {
              if (!activeOccupancyTab) return null;
              const inst = instructors.find(i => Number(i.id) === Number(activeOccupancyTab));
              if (!inst) return null;

              const instBatches = allBatches.filter(batch => 
                (Number(batch.instructorId) === Number(activeOccupancyTab) ||
                batch.instructors?.some(i => Number(i.id) === Number(activeOccupancyTab))) &&
                batch.status !== 'completed'
              );

              const dailyCount = instBatches.filter(b => (b.daysType || '').toLowerCase() === 'daily').length;
              const weekendCount = instBatches.filter(b => (b.daysType || '').toLowerCase() === 'weekend').length;

              const initials = inst.name ? inst.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'IN';
              const avatarStyle = getAvatarBgColor(inst.name);

              return (
                <div className="bg-white border border-slate-150 rounded-xl p-5 shadow-2xs hover:shadow-xs hover:border-slate-350 transition-all duration-200">
                  {/* Profile Info Banner */}
                  <div className="flex items-center gap-3 mb-4 pb-3.5 border-b border-slate-100">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-black border flex-shrink-0 ${avatarStyle}`}>
                      {initials}
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="text-base font-black text-slate-900 truncate">{inst.name}</span>
                        {/* Batches occupancy summary badges */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-slate-100 border border-slate-205 text-slate-600 shadow-3xs">
                            {instBatches.length} Total
                          </span>
                          {instBatches.length > 0 && (
                            <>
                              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 shadow-3xs">
                                {dailyCount} Daily
                              </span>
                              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 shadow-3xs">
                                {weekendCount} Weekend
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-slate-550 font-bold truncate">{inst.email || 'Instructor Account'}</span>
                    </div>
                  </div>

                  {/* Batch Schedules list */}
                  {instBatches.length === 0 ? (
                    <div className="flex items-center gap-2 text-xs text-slate-600 font-bold py-3 px-4 bg-slate-50 border border-slate-150 rounded-lg">
                      <FaInfoCircle className="text-slate-500 flex-shrink-0 text-base" />
                      <span>No commitment/schedule conflict found. Instructor is fully available!</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {instBatches.map((batch, bIdx) => (
                        <div key={bIdx} className="bg-slate-50/70 border border-slate-200 p-3.5 rounded-xl text-xs text-slate-700 hover:bg-slate-50 transition-colors">
                          <div className="flex justify-between items-start flex-wrap gap-2">
                            <div className="flex flex-col gap-1">
                              <span className="font-black text-slate-900 text-sm leading-snug">{batch.courseName}</span>
                              <span className="text-slate-800 text-[13px] font-bold">Batch: {batch.batchName}</span>
                            </div>
                            <span className={`text-[9px] font-black tracking-wider uppercase px-2.5 py-1 rounded-full border ${
                              batch.status === 'ongoing' 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                              {batch.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-slate-600 mt-3.5 border-t border-slate-200 pt-3 font-black">
                            <span className="flex items-center gap-1.5">
                              <FaClock className="text-xs text-slate-450" />
                              {batch.startTime} - {batch.endTime} ({batch.daysType || 'Daily'})
                            </span>
                            <span className="flex items-center gap-1.5">
                              <FaUser className="text-xs text-slate-450" />
                              {batch.students?.length || 0} Students Enrolled
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="mt-6 border-t border-slate-200 pt-4 flex justify-end">
            <button
              onClick={() => setShowOccupancyModal(false)}
              className="px-6 py-2.5 rounded-lg text-sm font-black bg-slate-700 hover:bg-slate-800 text-white shadow-md transition-all duration-200 cursor-pointer"
            >
              Close Schedules
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default GroupBatchCreator;
